import { Box, Typography } from '@mui/material';

/**
 * Shared Google Vision OCR cost UI (Trello #317) — used by the Dashboard Recent
 * Activity table AND the shared JobRunsTable so the two never drift.
 *
 * A run's Cost is the true all-in total (LLM token cost + OCR page cost). These
 * helpers render the split: a hover tooltip on the Cost cell + a breakdown line
 * in the expand row, shown only when the run had pages OCR'd via paid Vision.
 */

/** "20 pages" / "1 page" pluralisation, shared by the tooltip + the breakdown row. */
export function pagesLabel(ocrPages: number): string {
  return `${ocrPages} page${ocrPages === 1 ? '' : 's'}`;
}

/** Tooltip title for a Cost cell whose total includes OCR: "LLM $x + OCR $y (N pages)". */
export function ocrCostTooltipTitle(llmCostUsd: number, ocrCostUsd: number, ocrPages: number): string {
  return `LLM $${llmCostUsd.toFixed(4)} + OCR $${ocrCostUsd.toFixed(4)} (${pagesLabel(ocrPages)})`;
}

/**
 * Expand-row line showing the paid Google Vision OCR cost + page count.
 * Renders nothing when the run had no paid OCR (`ocrPages` falsy — a digital PDF
 * handled by the free pdftotext path).
 */
export function OcrCostBreakdown({
  ocrCostUsd,
  ocrPages,
}: {
  ocrCostUsd?: number;
  ocrPages?: number;
}) {
  if (!ocrPages) return null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography variant="caption" color="text.secondary">
        OCR
      </Typography>
      <Typography variant="body2">
        ${(ocrCostUsd ?? 0).toFixed(4)} · {pagesLabel(ocrPages)}
      </Typography>
    </Box>
  );
}
